// Reservado para Partners B2B (tiendas, fisios, cafés)
// Ruta oculta: no aparece en el sidebar del admin.
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X, CheckCircle, MapPin, Pencil, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/admin/locales")({
  component: AdminPoints,
});

type DPType = "bar" | "bike_shop" | "cafe" | "neutral" | "gym";
const TYPE_LABELS: Record<DPType, string> = {
  bar: "Bar", bike_shop: "Bike shop", cafe: "Café", neutral: "Punto neutral", gym: "Gimnasio",
};
const TYPE_COLOR: Record<DPType, string> = {
  bar: "#f59e0b", bike_shop: "#60a5fa", cafe: "#fb923c", neutral: "#9ca3af", gym: "#a78bfa",
};
interface DP {
  id: string; name: string; type: DPType; address: string; city: string;
  lat: number; lng: number; description: string | null; image_url: string | null;
  is_active: boolean;
}

function AdminPoints() {
  const [rows, setRows] = useState<DP[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<DP | null>(null);
  const [deleting, setDeleting] = useState<DP | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("departure_points")
      .select("*")
      .order("city", { ascending: true });
    if (error) toast.error(error.message);
    setRows((data as DP[]) || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (row: DP) => {
    const { error } = await supabase
      .from("departure_points")
      .update({ is_active: !row.is_active })
      .eq("id", row.id);
    if (error) toast.error(error.message);
    else { toast.success(row.is_active ? "Desactivado" : "Activado"); load(); }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    const { error } = await supabase.from("departure_points").delete().eq("id", deleting.id);
    if (error) toast.error(error.message);
    else { toast.success("Punto eliminado"); load(); }
    setDeleting(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl">Puntos de salida</h1>
          <p className="text-sm text-muted-foreground">Bares, talleres y cafés verificados.</p>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="press-scale flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground"
        >
          <Plus className="h-4 w-4" /> Añadir punto
        </button>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-surface">
        <div className="grid grid-cols-[1.4fr_0.7fr_1fr_0.6fr_1.2fr] gap-3 border-b border-border bg-[#0f0f0f] px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground">
          <div>Nombre</div><div>Tipo</div><div>Ciudad</div><div>Estado</div><div className="text-right">Acciones</div>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-muted-foreground">Cargando…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Sin puntos todavía.</div>
        ) : rows.map((r) => (
          <div key={r.id} className="grid grid-cols-[1.4fr_0.7fr_1fr_0.6fr_1.2fr] gap-3 items-center border-b border-border px-4 py-3 text-sm last:border-0">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 truncate font-medium">
                <MapPin className="h-3.5 w-3.5 text-primary shrink-0" /> {r.name}
              </div>
              <div className="truncate text-xs text-muted-foreground">{r.address}</div>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <span className="h-2 w-2 rounded-full" style={{ background: TYPE_COLOR[r.type] }} />
              {TYPE_LABELS[r.type] ?? r.type}
            </div>
            <div className="text-xs">{r.city}</div>
            <div>
              {r.is_active ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-400">
                  <CheckCircle className="h-3 w-3" /> Activo
                </span>
              ) : (
                <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] text-white/60">Inactivo</span>
              )}
            </div>
            <div className="flex justify-end gap-1.5 flex-wrap">
              <button
                onClick={() => toggleActive(r)}
                className="rounded-full border border-border bg-surface-elevated px-3 py-1 text-xs hover:bg-white/5"
              >
                {r.is_active ? "Desactivar" : "Activar"}
              </button>
              <button
                onClick={() => setEditing(r)}
                className="inline-flex items-center gap-1 rounded-full border border-border bg-surface-elevated px-3 py-1 text-xs hover:bg-white/5"
              >
                <Pencil className="h-3 w-3" /> Editar
              </button>
              <button
                onClick={() => setDeleting(r)}
                className="inline-flex items-center gap-1 rounded-full border border-red-500/30 bg-red-500/10 px-3 py-1 text-xs text-red-400 hover:bg-red-500/20"
              >
                <Trash2 className="h-3 w-3" /> Eliminar
              </button>
            </div>
          </div>
        ))}
      </div>

      {open && <PointDialog onClose={() => { setOpen(false); load(); }} />}
      {editing && <PointDialog point={editing} onClose={() => { setEditing(null); load(); }} />}
      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm fade-in" onClick={() => setDeleting(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-3xl border border-border bg-[#1a1a1a] p-5 mx-4">
            <h3 className="font-display text-xl">Eliminar punto</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              ¿Seguro que quieres eliminar <span className="text-white">{deleting.name}</span>? Esta acción no se puede deshacer.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setDeleting(null)} className="rounded-full border border-border bg-surface-elevated px-4 py-2 text-sm">Cancelar</button>
              <button onClick={confirmDelete} className="rounded-full bg-red-500 px-4 py-2 text-sm font-bold text-white hover:bg-red-600">Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PointDialog({ point, onClose }: { point?: DP; onClose: () => void }) {
  const isEdit = !!point;
  const [name, setName] = useState(point?.name ?? "");
  const [type, setType] = useState<DPType>(point?.type ?? "bar");
  const [address, setAddress] = useState(point?.address ?? "");
  const [city, setCity] = useState(point?.city ?? "");
  const [description, setDescription] = useState(point?.description ?? "");
  const [imageUrl, setImageUrl] = useState(point?.image_url ?? "");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<{ lat: number; lng: number; address: string }[]>([]);
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(
    point ? { lat: point.lat, lng: point.lng } : null,
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (coords || query.length < 3) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&limit=5&countrycodes=es&q=${encodeURIComponent(query)}`,
          { headers: { "Accept-Language": "es" } },
        );
        const data = await res.json();
        setResults(data.map((d: any) => ({ lat: parseFloat(d.lat), lng: parseFloat(d.lon), address: d.display_name })));
      } catch {}
    }, 400);
    return () => clearTimeout(t);
  }, [query, coords]);

  const save = async () => {
    if (!name || !address || !city || !coords) {
      toast.error("Faltan campos requeridos.");
      return;
    }
    setSaving(true);
    const payload = {
      name, type, address, city,
      lat: coords.lat, lng: coords.lng,
      description: description || null,
      image_url: imageUrl || null,
    };
    const { error } = isEdit
      ? await supabase.from("departure_points").update(payload).eq("id", point!.id)
      : await supabase.from("departure_points").insert(payload);
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success(isEdit ? "Punto actualizado" : "Punto añadido");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/70 backdrop-blur-sm fade-in" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full md:max-w-lg max-h-[90vh] overflow-y-auto rounded-t-3xl md:rounded-3xl border border-border bg-[#1a1a1a] p-5"
      >
        <div className="flex items-start justify-between">
          <h2 className="font-display text-2xl">{isEdit ? "Editar punto" : "Nuevo punto"}</h2>
          <button onClick={onClose} className="rounded-full bg-surface-elevated p-1.5"><X className="h-4 w-4" /></button>
        </div>

        <div className="mt-4 space-y-3">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre (Bar El Pedal)"
            className="w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-sm" />
          <select value={type} onChange={(e) => setType(e.target.value as DPType)}
            className="w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-sm">
            <option value="bar">Bar</option>
            <option value="bike_shop">Bike shop</option>
            <option value="cafe">Café</option>
            <option value="gym">Gimnasio</option>
            <option value="neutral">Punto neutral</option>
          </select>
          <div className="grid grid-cols-2 gap-2">
            <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Dirección"
              className="rounded-xl border border-border bg-surface px-3 py-2.5 text-sm" />
            <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Ciudad"
              className="rounded-xl border border-border bg-surface px-3 py-2.5 text-sm" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Ubicación (lat/lng)</label>
            <input
              value={coords ? `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}` : query}
              onChange={(e) => { setQuery(e.target.value); setCoords(null); }}
              placeholder="Buscar dirección en Nominatim…"
              className="mt-1 w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-sm"
            />
            {!coords && results.length > 0 && (
              <div className="mt-1 overflow-hidden rounded-xl border border-border bg-surface">
                {results.map((r, i) => (
                  <button key={i} onClick={() => { setCoords({ lat: r.lat, lng: r.lng }); setQuery(""); setResults([]); }}
                    className="block w-full border-b border-border px-3 py-2 text-left text-xs last:border-0 hover:bg-surface-elevated">
                    {r.address}
                  </button>
                ))}
              </div>
            )}
          </div>

          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
            placeholder="Descripción (opcional)"
            className="w-full resize-none rounded-xl border border-border bg-surface px-3 py-2.5 text-sm" />
          <input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="URL imagen (opcional)"
            className="w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-sm" />

          <button
            onClick={save}
            disabled={saving}
            className="press-scale w-full rounded-xl bg-primary py-3 font-bold text-primary-foreground disabled:opacity-50"
          >
            {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Guardar punto"}
          </button>
        </div>
      </div>
    </div>
  );
}
